/**
 * Searching the whole organisation's audit log, and writing it out as CSV
 * (PRD 11.2 and 13.4, the compliance officer's "searchable, exportable log of
 * all task completions with actor, timestamp, and employee").
 *
 * The organisation's object reads the rows and joins in what each one is
 * about -- the person, the site, the card's title and department -- and these
 * functions decide what matches and what the file says. Nothing here reads
 * the clock or the storage.
 */

import type { Actor } from "./engine.js";
import type { LifecycleKind } from "./template.js";

/** What the log can be narrowed to, besides the words searched for. */
export const AUDIT_KINDS = {
  all: "Everything",
  completed: "Cards finished",
  skipped: "Cards skipped",
  pii: "Private values viewed",
  export: "Exports",
} as const;

export type AuditKind = keyof typeof AUDIT_KINDS;

export interface AuditQuery {
  /** Every word has to appear in the person's name, the card's title, or who did it. */
  text: string;
  kind: AuditKind;
  siteId: string | null;
  /** The card's owning department. */
  department: string | null;
  /** Milliseconds, inclusive; the app turns the dates it was given into its own zone's midnights. */
  since: number | null;
  until: number | null;
}

export const EMPTY_AUDIT_QUERY: AuditQuery = {
  text: "",
  kind: "all",
  siteId: null,
  department: null,
  since: null,
  until: null,
};

/** One row of the log, with what it was about joined in. */
export interface AuditHit {
  seq: number;
  at: number;
  actorId: string;
  actorName: string;
  actorDepartment: string;
  action: string;
  instanceId: string | null;
  kind: LifecycleKind | null;
  employeeName: string | null;
  siteId: string | null;
  siteName: string | null;
  taskKey: string | null;
  taskTitle: string | null;
  /** The card's owning department, not the actor's. */
  department: string | null;
  from: string | null;
  to: string | null;
  note: string | null;
  ip: string | null;
  hash: string;
}

/** Admins and compliance officers, and nobody else, take the log away (PRD 11.2). HR can search it. */
export const canExportAudit = (actor: Pick<Actor, "role">): boolean =>
  actor.role === "admin" || actor.role === "compliance";

const MAX_TEXT = 200;

const when = (value: unknown): number | null | undefined => {
  if (value === undefined || value === null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? Math.floor(number) : undefined;
};

const choice = (value: unknown): string | null | undefined => {
  if (value === undefined || value === null || value === "") return null;
  return typeof value === "string" && value.length <= MAX_TEXT ? value : undefined;
};

/** A query as sent, from a search's address or an export's body, or the sentence saying what is wrong. */
export function parseAuditQuery(
  raw: Record<string, unknown> | null,
): { ok: true; query: AuditQuery } | { ok: false; reason: string } {
  const source = raw ?? {};
  const text = source.text ?? "";
  if (typeof text !== "string" || text.length > MAX_TEXT)
    return { ok: false, reason: "Search for fewer than 200 characters." };
  const kind = source.kind ?? "all";
  if (typeof kind !== "string" || !(kind in AUDIT_KINDS))
    return { ok: false, reason: "That is not one of the kinds of entry the log can show." };
  const siteId = choice(source.siteId);
  const department = choice(source.department);
  if (siteId === undefined || department === undefined)
    return { ok: false, reason: "That site or department is not one this organisation has." };
  const since = when(source.since);
  const until = when(source.until);
  if (since === undefined || until === undefined) return { ok: false, reason: "That date is not a date." };
  if (since !== null && until !== null && since > until)
    return { ok: false, reason: "The first date is after the last one." };
  return {
    ok: true,
    query: { text: text.trim(), kind: kind as AuditKind, siteId, department, since, until },
  };
}

const KIND_TEST: Record<AuditKind, (hit: AuditHit) => boolean> = {
  all: () => true,
  completed: (hit) => hit.action === "state" && hit.to === "completed",
  skipped: (hit) => hit.action === "state" && hit.to === "skipped",
  pii: (hit) => hit.action === "pii_view",
  export: (hit) => hit.action === "export",
};

/**
 * Whether a row is one the query asked for. Words are matched without regard
 * to case, each anywhere in the person's name, the card's title or the
 * actor's name, so "dana i-9" finds Dana Reyes's I-9 verification.
 */
export function auditMatches(hit: AuditHit, query: AuditQuery): boolean {
  if (!KIND_TEST[query.kind](hit)) return false;
  if (query.since !== null && hit.at < query.since) return false;
  if (query.until !== null && hit.at > query.until) return false;
  if (query.siteId !== null && hit.siteId !== query.siteId) return false;
  if (query.department !== null && hit.department !== query.department) return false;
  const words = query.text.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = [hit.employeeName, hit.taskTitle, hit.actorName].join("\n").toLowerCase();
  return words.every((word) => haystack.includes(word));
}

/** What an export says it was of, for the log row that records it and for the file's own first lines. */
export function auditQueryText(query: AuditQuery, siteName: string | null): string {
  const parts: string[] = [AUDIT_KINDS[query.kind]];
  if (query.text) parts.push(`matching "${query.text}"`);
  if (siteName) parts.push(`at ${siteName}`);
  if (query.department) parts.push(`owned by ${query.department}`);
  if (query.since !== null) parts.push(`from ${new Date(query.since).toISOString()}`);
  if (query.until !== null) parts.push(`to ${new Date(query.until).toISOString()}`);
  return parts.join(", ");
}

/**
 * A cell as a spreadsheet will take it: quoted when it has to be, and a value
 * that starts like a formula (`=`, `+`, `-`, `@`) led with an apostrophe, so
 * a card titled "=HYPERLINK(...)" opens as text rather than running.
 */
export function csvCell(value: string | number | null): string {
  if (value === null) return "";
  let text = String(value);
  if (typeof value === "string" && /^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

const COLUMNS: readonly [string, (hit: AuditHit) => string | number | null][] = [
  ["seq", (hit) => hit.seq],
  ["timestamp_utc", (hit) => new Date(hit.at).toISOString()],
  ["actor_id", (hit) => hit.actorId],
  ["actor_name", (hit) => (hit.actorId === "system" ? "utuwrk" : hit.actorName)],
  ["actor_department", (hit) => hit.actorDepartment],
  ["action", (hit) => hit.action],
  ["workflow", (hit) => hit.kind],
  ["workflow_id", (hit) => hit.instanceId],
  ["employee", (hit) => hit.employeeName],
  ["site", (hit) => hit.siteName],
  ["card", (hit) => hit.taskTitle],
  ["card_key", (hit) => hit.taskKey],
  ["card_department", (hit) => hit.department],
  ["previous_value", (hit) => hit.from],
  ["new_value", (hit) => hit.to],
  ["note", (hit) => hit.note],
  ["ip", (hit) => hit.ip],
  ["hash", (hit) => hit.hash],
];

/** The log as CSV, oldest first, with every PRD 11.1 field this log keeps (13.4). */
export function auditCsv(hits: readonly AuditHit[]): string {
  const lines = [COLUMNS.map(([name]) => name).join(",")];
  for (const hit of [...hits].sort((a, b) => a.seq - b.seq))
    lines.push(COLUMNS.map(([, value]) => csvCell(value(hit))).join(","));
  return `${lines.join("\r\n")}\r\n`;
}
